import React, { useMemo } from 'react';
import {
  Props as ContextProps,
  IndexScrollbarContextProvider,
} from '@/contexts/index-scrollbar-context';
import {
  ItemListOption,
  ItemListOptionSection,
  ItemSortOrder,
} from '@/contexts/item-context-types';

const ENABLED_SORT_ORDERS: ItemSortOrder[] = [
  ItemSortOrder.Alphabetical,
  ItemSortOrder.AlphabeticalReverse,
  ItemSortOrder.ItemCode,
  ItemSortOrder.ItemCodeReverse,
  ItemSortOrder.PurchasedDate,
];

const MINIMUM_ITEMS_LIMIT = 10;
export const MISCELLANEOUS_INDEX = '-';

// Items sorted by the database ignores diacritics and special characters,
// so those are filtered out to determine the normalized index
const NORMALIZE_REGEX = /([\u0300-\u036f]|[^a-zA-Z0-9])/g;
const getNormalizedIndex = (value: string) => {
  return (
    value.normalize('NFD').replace(NORMALIZE_REGEX, '')[0] ||
    MISCELLANEOUS_INDEX
  ).toUpperCase();
};

interface Props {
  children?: React.ReactNode;
  onScrollToIndex: (index: number) => void;
  sections: ItemListOptionSection[];
  sortOrder: ItemSortOrder;
}

export const OrderableItemScrollbarContainer: React.FC<Props> = ({
  children,
  sections,
  onScrollToIndex,
  sortOrder,
}) => {
  const contextProps: ContextProps = useMemo(() => {
    const items = sections.reduce<ItemListOption[]>((result, section) => {
      return [...result, ...section.data];
    }, []);
    const isItemCodeSortOrder = (
      [ItemSortOrder.ItemCode, ItemSortOrder.ItemCodeReverse] as ItemSortOrder[]
    ).includes(sortOrder);

    const indexes: string[] = [];
    const indexMap = items.reduce<{ [index: string]: number }>(
      (result, item, i) => {
        const index = getNormalizedIndex(
          isItemCodeSortOrder ? item.metadata?.externalID || '' : item.title,
        );
        if (typeof result[index] !== 'number') {
          indexes.push(index);

          // Section headers offset the index by 1
          const hasSectionKey = Boolean(sections[0]?.key);
          result[index] = hasSectionKey ? i + 1 : i;
        }

        return result;
      },
      {},
    );

    return {
      enabled:
        sections.length === 1 &&
        ENABLED_SORT_ORDERS.includes(sortOrder) &&
        items.length >= MINIMUM_ITEMS_LIMIT,
      indexes,
      indexMap,
      onScrollToIndex,
    };
  }, [sections, onScrollToIndex, sortOrder]);

  return (
    <IndexScrollbarContextProvider {...contextProps}>
      {children}
    </IndexScrollbarContextProvider>
  );
};
